import { Cartesian3, Color, PolylineCollection, ConstantPositionProperty, Material, BoundingSphere, } from "cesium";
import { DEFAULT_ROAD_LABEL_COLOR } from "../road/defaults";
import { CurveDataType, } from "../../core/alignment/horizontal-alignment/interface";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { AssetType, CESIUM_PICKABLE_ENTITY, } from "../../types";
import { AlignmentPointType, } from "../../core/alignment/interface";
import { Site } from "../site";
import { getDefaultSectionMarkerColorSettings } from "../../settings/earthbrain-site-settings";
export class CesiumRenderableAlignment {
    _alignment;
    _viewer;
    _polylineCollection;
    _settings;
    _ipLinePoints;
    _centerLinePoints;
    _curveSegments;
    _surveyPointMap;
    _boundingSphere;
    _isCenterLineVisible = false;
    //RENDER Entities
    _renderData;
    _curveColor;
    _sphereColor;
    constructor(alignment, viewer, settings) {
        this._alignment = alignment;
        this._viewer = viewer;
        this._renderData = {
            ipPointSpheres: new Map(),
            ipPointLabels: new Map(),
            centerLineSpheres: new Map(),
            centerLineLabels: new Map(),
            centerCurvePolylines: [],
            ipLine: undefined,
        };
        this._ipLinePoints = [];
        this._centerLinePoints = [];
        this._curveSegments = [];
        this._surveyPointMap = new Map();
        this._polylineCollection = new PolylineCollection();
        this._viewer.scene.primitives.add(this._polylineCollection);
        this._settings = {
            width: settings?.width ?? 2,
            color: settings?.color ?? Color.WHITE,
            outlineWidth: settings?.outlineWidth ?? 0,
            outlineColor: settings?.outlineColor ?? Color.BLACK,
        };
        this._curveColor = {
            [CurveDataType.LINEAR]: Color.YELLOW,
            [CurveDataType.QUADRATIC_BEZIER]: Color.BLUE,
            [CurveDataType.THREE_POINT_ARC]: Color.GREEN,
            [CurveDataType.CLOTHOID]: Color.RED,
            [CurveDataType.CIRCULAR_SEGMENT]: Color.ORANGE,
        };
        const sectionMarkerColorSettings = getDefaultSectionMarkerColorSettings();
        this._sphereColor = {
            [CESIUM_PICKABLE_ENTITY.ALIGNMENT_IP_POINT]: sectionMarkerColorSettings.ipControlPointColor,
            [CESIUM_PICKABLE_ENTITY.ALIGNMENT_POINT]: sectionMarkerColorSettings.markerColor,
        };
    }
    // ----------------getter  methods--------------------
    get id() {
        return this._alignment.id;
    }
    get assetType() {
        return this._alignment.assetType;
    }
    get2dViewerData(site) {
        return this._alignment.get2dViewerData(site);
    }
    get pointsToCreateCrossSection() {
        return this._alignment.pointsToCreateCrossSection;
    }
    getDirectionAtCenterlinePoint(pointId) {
        return this._alignment.getDirectionAtCenterlinePoint(pointId);
    }
    getCesiumSphereEntity(id) {
        const entity = this._renderData.ipPointSpheres.get(id) ||
            this._renderData.centerLineSpheres.get(id);
        return entity;
    }
    /** Looks up a center-line sphere only, ignoring an IP control point sphere with the same id. */
    getCesiumCenterLineSphereEntity(id) {
        return this._renderData.centerLineSpheres.get(id);
    }
    get boundingSphere() {
        return this._boundingSphere;
    }
    get ipLinePoints() {
        return this._ipLinePoints;
    }
    // ----------------Init methods --------------------
    async initializeFromAlignmentData(surveyWGS84Points, ipPoints, horizontalAlignmentData, verticalAlignmentData, stationInterval) {
        const localPoints = await Site.getInstance().convertPointsToLocalSpace(surveyWGS84Points.map((p) => p.wgs84Point));
        const surveyPoints = surveyWGS84Points.map((surveyPoint, index) => {
            return {
                id: surveyPoint.id,
                name: surveyPoint.name,
                localPoint: localPoints[index],
                wgs84Point: surveyPoint.wgs84Point,
            };
        });
        const ipPointIds = this._alignment.initializeFromAlignmentData(surveyPoints, ipPoints, horizontalAlignmentData, verticalAlignmentData, stationInterval);
        //fill up survey points
        // survey points in alignment can increase
        const surveyLocalPoints = this._alignment.surveyPoints;
        const surveyWGS84PointMap = new Map();
        surveyWGS84Points.forEach((surveyPoint) => {
            surveyWGS84PointMap.set(surveyPoint.id, surveyPoint.wgs84Point);
        });
        const surveyLocalPointMapToBeConverted = [];
        this._surveyPointMap.clear();
        surveyLocalPoints.forEach((surveyLocalPoint) => {
            const wgs84Point = surveyWGS84PointMap.get(surveyLocalPoint.id);
            const surveyPoint = {
                id: surveyLocalPoint.id,
                name: surveyLocalPoint.name,
                localPoint: surveyLocalPoint.localPoint,
                wgs84Point: wgs84Point,
            };
            if (!wgs84Point) {
                surveyLocalPointMapToBeConverted.push(surveyPoint);
            }
            this._surveyPointMap.set(surveyLocalPoint.id, surveyPoint);
        });
        if (surveyLocalPointMapToBeConverted.length > 0) {
            const convertedPoints = await Site.getInstance().convertPointsToWGS84(surveyLocalPointMapToBeConverted.map((p) => p.localPoint));
            for (let index = 0; index < convertedPoints.length; index++) {
                surveyLocalPointMapToBeConverted[index].wgs84Point =
                    convertedPoints[index];
            }
        }
        return ipPointIds;
    }
    // -----------------public methods --------------------
    /**
     * Add an IP point at the given index to the alignment and register its WGS84 position.
     * @param index INDEX at which the IP point will be added
     * @param pointInfo POSITION of the IP point in local and/or WGS84 space
     * @param restoreIpId undo-only: reuse the deleted IP point's ID (middle insertion only)
     * @returns ID of the added IP point
     */
    async addIPPointAtIndex(index, pointInfo, restoreIpId) {
        if (!pointInfo.localPosition && !pointInfo.wgs84Position) {
            throw new Error("Invalid point provided to addIPPointAtIndex");
        }
        let localPos = pointInfo.localPosition;
        let wgs84Pos = pointInfo.wgs84Position;
        const site = Site.getInstance();
        if (!localPos && wgs84Pos) {
            const [convertedLocalPos] = await site.convertPointsToLocalSpace([
                wgs84Pos,
            ]);
            localPos = convertedLocalPos;
        }
        if (localPos && !wgs84Pos) {
            const [convertedWgs84Pos] = await site.convertPointsToWGS84([localPos]);
            wgs84Pos = convertedWgs84Pos;
        }
        const id = this._alignment.addIPPointAtIndex(index, localPos, restoreIpId);
        if (id) {
            const surveyPoint = this._alignment.surveyPoints.find((p) => p.id === id);
            if (surveyPoint) {
                this._surveyPointMap.set(surveyPoint.id, {
                    ...surveyPoint,
                    wgs84Point: wgs84Pos,
                });
            }
            const ipPoint = this._alignment.ipLinePoints.find((p) => p.id === id);
            if (ipPoint) {
                this._ipLinePoints.push({
                    ...ipPoint,
                    wgs84Point: wgs84Pos,
                    wgs84PointWithAdjustedHeight: wgs84Pos.clone(),
                });
            }
        }
        return id;
    }
    /**
     * 曲線生成で作られた測点（BC / EC）の高さを縦断から補完し、WGS84 位置を登録する。
     * 未登録の測点のみ変換するため、IP 点の WGS84 位置は保持される。
     */
    async applyGeneratedSurveyPointHeights() {
        this._alignment.applyVerticalProfileToGeneratedSurveyPoints();
        const pointsToRegister = this._alignment.surveyPoints.filter((surveyPoint) => !this._surveyPointMap.has(surveyPoint.id));
        if (pointsToRegister.length === 0) {
            return;
        }
        const wgs84Points = await Site.getInstance().convertPointsToWGS84(pointsToRegister.map((surveyPoint) => surveyPoint.localPoint));
        pointsToRegister.forEach((surveyPoint, index) => {
            this._surveyPointMap.set(surveyPoint.id, {
                id: surveyPoint.id,
                name: surveyPoint.name,
                localPoint: surveyPoint.localPoint,
                wgs84Point: wgs84Points[index],
            });
        });
    }
    async updateIPPointPosition(pointInfo, clickedControlPointID) {
        this._alignment.updateIPPointPosition(pointInfo, clickedControlPointID);
        const surveyPoint = this._surveyPointMap.get(clickedControlPointID);
        if (surveyPoint) {
            Cartesian3.clone(pointInfo.position, surveyPoint.wgs84Point);
        }
        const ipPoint = this._ipLinePoints.find((p) => p.id === clickedControlPointID);
        if (ipPoint) {
            Cartesian3.clone(pointInfo.position, ipPoint.wgs84Point);
            const alignedPoint = this._alignment.ipLinePoints.find((p) => p.id === clickedControlPointID);
            if (alignedPoint) {
                const [wgs84PointAdjusted] = await Site.getInstance().convertPointsToWGS84([
                    alignedPoint.localPointWithAdjustedHeight,
                ]);
                Cartesian3.clone(wgs84PointAdjusted, ipPoint.wgs84PointWithAdjustedHeight);
            }
        }
    }
    removeIPPoint(clickedControlPointID) {
        this._alignment.removeIPPoint(clickedControlPointID);
        const index = this._ipLinePoints.findIndex((p) => p.id === clickedControlPointID);
        if (index === -1) {
            return;
        }
        this._ipLinePoints.splice(index, 1);
    }
    toJSON() {
        const { horizontalAlignment, verticalAlignment } = this._alignment.toJSON();
        return {
            horizontalAlignment,
            verticalAlignment,
            surveyPoints: Array.from(this._surveyPointMap.values()).map((p) => ({
                wgs84Point: p.wgs84Point,
                id: p.id,
                name: p.name,
            })),
            ipPointIds: this._ipLinePoints.map((p) => p.id),
        };
    }
    dispose() {
        this.disposeRenderedEntities();
        this._viewer.scene.primitives.remove(this._polylineCollection);
        if (!this._polylineCollection.isDestroyed()) {
            this._polylineCollection.removeAll();
            this._polylineCollection.destroy();
        }
        this._polylineCollection = undefined;
    }
    async render() {
        this.disposeRenderedEntities();
        await this.computePreRenderData();
        // IP points, label, line
        this.renderIPPointSpheres();
        this.renderIPPointLabels();
        this._updateIPLinePolyline();
        //Centreline points, label, line
        this.renderCenterLinePointSpheres();
        this.renderCenterLinePointLabels();
        this._updateCenterPolylines();
    }
    renderIPPointLabels(labelcolor = DEFAULT_ROAD_LABEL_COLOR) {
        this.disposeIPPointLabels();
        for (let i = 0; i < this._ipLinePoints.length; i++) {
            const ipPoint = this._ipLinePoints[i];
            const label = EarthBrainUtils.addLabel(this._viewer, ipPoint.wgs84PointWithAdjustedHeight, ipPoint.label, {
                fillColor: Color.fromCssColorString(labelcolor),
            });
            this._renderData.ipPointLabels.set(ipPoint.id, label);
        }
    }
    isVisible() {
        const isIPShow = this._renderData.ipLine?.show ?? false;
        const isCenterShow = this._renderData.centerCurvePolylines[0]?.show ?? false;
        return isIPShow || isCenterShow;
    }
    setIpLineVisibility(show) {
        this._renderData.ipPointSpheres.forEach((value) => {
            value.show = show;
        });
        this._renderData.ipPointLabels.forEach((value) => {
            value.show = show;
        });
        if (this._renderData.ipLine) {
            this._renderData.ipLine.show = show;
        }
    }
    get isCenterLineVisible() {
        return this._isCenterLineVisible;
    }
    setCenterLineVisibility(show) {
        this._isCenterLineVisible = show;
        this._renderData.centerLineSpheres.forEach((value) => {
            value.show = show;
        });
        this._renderData.centerLineLabels.forEach((value) => {
            value.show = show;
        });
        this._renderData.centerCurvePolylines.forEach((value) => {
            value.show = show;
        });
        if (this._renderData.ipLine &&
            this._renderData.ipPointLabels.size > 0 &&
            this._renderData.ipPointSpheres.size > 0 &&
            !this._renderData.ipLine.show) {
            const labels = Array.from(this._renderData.ipPointLabels.values());
            labels[0].show = show;
            labels[labels.length - 1].show = show;
            const spheres = Array.from(this._renderData.ipPointSpheres.values());
            spheres[0].show = show;
            spheres[spheres.length - 1].show = show;
        }
    }
    setVisibility(show) {
        this.setCenterLineVisibility(show);
        this.setIpLineVisibility(show);
    }
    getIPSphereEntity(id) {
        return this._renderData.ipPointSpheres.get(id);
    }
    async updateStationInterval(stationInterval) {
        if (this._alignment.stationInterval === stationInterval) {
            return;
        }
        this._alignment.updateStationInterval(stationInterval);
        await this.render();
    }
    // ------------------private methods ----------------------
    async computeWGS84Points() {
        const wgs84IPPoints = await Site.getInstance().convertPointsToWGS84(this._alignment.ipLinePoints.map((ipPoint) => ipPoint.localPointWithAdjustedHeight));
        this._ipLinePoints = this._alignment.ipLinePoints.map((ipPoint, i) => {
            return {
                ...ipPoint,
                wgs84PointWithAdjustedHeight: wgs84IPPoints[i],
                wgs84Point: wgs84IPPoints[i],
            };
        });
        // Station Points, BC, EC, PVI, IP projections
        const centerLinePointsToRender = this._alignment.centerLinePoints.filter((centerLinePoint) => {
            return (centerLinePoint.pointType.includes(AlignmentPointType.STATION_POINT) ||
                centerLinePoint.pointType.includes(AlignmentPointType.BEGIN_CURVE_POINT) ||
                centerLinePoint.pointType.includes(AlignmentPointType.END_CURVE_POINT) ||
                centerLinePoint.pointType.includes(AlignmentPointType.POINT_OF_VERTICAL_INTERSECTION) ||
                centerLinePoint.pointType.includes(AlignmentPointType.IP_PROJECTION));
        });
        const wgs84CenterLinePoints = await Site.getInstance().convertPointsToWGS84(centerLinePointsToRender.map((centerLinePoint) => centerLinePoint.localPointWithAdjustedHeight));
        this._centerLinePoints = centerLinePointsToRender.map((centerLinePoint, i) => {
            return {
                ...centerLinePoint,
                wgs84PointWithAdjustedHeight: wgs84CenterLinePoints[i],
                wgs84Point: wgs84CenterLinePoints[i],
            };
        });
        this._curveSegments.length = 0;
        for (const curvePoints of this._alignment.curvePoints) {
            const wgs84points = await Site.getInstance().convertPointsToWGS84(curvePoints.points);
            if (wgs84points) {
                this._curveSegments.push({
                    type: curvePoints.type,
                    points: wgs84points,
                });
            }
        }
    }
    computeBoundingSphere() {
        this._boundingSphere = BoundingSphere.fromPoints(this._ipLinePoints.map((ipPoint) => ipPoint.wgs84PointWithAdjustedHeight));
    }
    async computePreRenderData() {
        await this.computeWGS84Points();
        this.computeBoundingSphere();
    }
    getAlignmentPointSphere(position, pointType, id) {
        const sphereEntity = EarthBrainUtils.addSphere(this._viewer, position, this._sphereColor[pointType], Number.POSITIVE_INFINITY, undefined, id);
        sphereEntity.addProperty("pickInfo"); //Information when this sphere gets picked
        sphereEntity["pickInfo"] = {
            assetType: AssetType.ALIGNMENT,
            pickableType: pointType,
            entity: sphereEntity,
            owner: this,
        };
        return sphereEntity;
    }
    renderIPPointSpheres() {
        const renderedSpheresIds = Array.from(this._renderData.ipPointSpheres.keys());
        this._ipLinePoints.forEach((ipPoint) => {
            if (this._renderData.ipPointSpheres.has(ipPoint.id)) {
                renderedSpheresIds.splice(renderedSpheresIds.indexOf(ipPoint.id), 1);
                this._renderData.ipPointSpheres.get(ipPoint.id).position =
                    new ConstantPositionProperty(ipPoint.wgs84PointWithAdjustedHeight);
            }
            else {
                const sphere = this.getAlignmentPointSphere(ipPoint.wgs84PointWithAdjustedHeight, CESIUM_PICKABLE_ENTITY.ALIGNMENT_IP_POINT, ipPoint.id);
                this._renderData.ipPointSpheres.set(ipPoint.id, sphere);
            }
        });
        for (const id of renderedSpheresIds) {
            const sphere = this._renderData.ipPointSpheres.get(id);
            if (sphere) {
                this._viewer.entities.remove(sphere);
            }
            this._renderData.ipPointSpheres.delete(id);
        }
    }
    renderCenterLinePointSpheres() {
        const renderedSpheresIds = Array.from(this._renderData.centerLineSpheres.keys());
        this._centerLinePoints.forEach((centerLinePoint) => {
            if (this._renderData.ipPointSpheres.has(centerLinePoint.id)) {
                return;
            }
            if (this._renderData.centerLineSpheres.has(centerLinePoint.id)) {
                renderedSpheresIds.splice(renderedSpheresIds.indexOf(centerLinePoint.id), 1);
                this._renderData.centerLineSpheres.get(centerLinePoint.id).position =
                    new ConstantPositionProperty(centerLinePoint.wgs84PointWithAdjustedHeight);
            }
            else {
                const sphere = this.getAlignmentPointSphere(centerLinePoint.wgs84PointWithAdjustedHeight, CESIUM_PICKABLE_ENTITY.ALIGNMENT_POINT, centerLinePoint.id);
                sphere.show = this._isCenterLineVisible;
                this._renderData.centerLineSpheres.set(centerLinePoint.id, sphere);
            }
        });
        for (const id of renderedSpheresIds) {
            const sphere = this._renderData.centerLineSpheres.get(id);
            if (sphere) {
                this._viewer.entities.remove(sphere);
            }
            this._renderData.centerLineSpheres.delete(id);
        }
    }
    renderCenterLinePointLabels(labelcolor = DEFAULT_ROAD_LABEL_COLOR) {
        this.disposeCenterLinePointLabels();
        for (let i = 1; i < this._centerLinePoints.length - 1; i++) {
            const centerLinePoint = this._centerLinePoints[i];
            const label = EarthBrainUtils.addLabel(this._viewer, centerLinePoint.wgs84PointWithAdjustedHeight, centerLinePoint.label, {
                fillColor: Color.fromCssColorString(labelcolor),
            });
            label.show = this._isCenterLineVisible;
            this._renderData.centerLineLabels.set(centerLinePoint.id, label);
        }
    }
    _updateIPLinePolyline() {
        if (this._renderData.ipLine &&
            this._polylineCollection.contains(this._renderData.ipLine)) {
            this._polylineCollection.remove(this._renderData.ipLine);
        }
        const positions = this._ipLinePoints.map((p) => p.wgs84PointWithAdjustedHeight);
        if (positions.length > 1) {
            this._renderData.ipLine = this._polylineCollection.add({
                name: "roadstraightline",
                positions: positions,
                width: this._settings.width,
            });
            this._renderData.ipLine.material = Material.fromType("PolylineOutline", {
                color: this._settings.color,
                outlineColor: this._settings.outlineColor,
                outlineWidth: this._settings.outlineWidth,
            });
            this._renderData.ipLine.show = true;
        }
    }
    _updateCenterPolylines() {
        this._renderData.centerCurvePolylines.forEach((p) => {
            if (p && this._polylineCollection.contains(p)) {
                this._polylineCollection.remove(p);
            }
        });
        this._renderData.centerCurvePolylines.length = 0;
        this._curveSegments.forEach((curveSegment) => {
            const polyline = this._polylineCollection.add({
                name: "roadcurvedashline",
                positions: curveSegment.points,
                width: this._settings.width,
            });
            polyline.material = Material.fromType("PolylineOutline", {
                color: this._curveColor[curveSegment.type],
                outlineColor: this._curveColor[curveSegment.type],
                outlineWidth: this._settings.outlineWidth,
                depthFailMaterial: Material.fromType("PolylineOutline", {
                    color: this._curveColor[curveSegment.type],
                    outlineColor: this._curveColor[curveSegment.type],
                    outlineWidth: this._settings.outlineWidth,
                }),
            });
            polyline.show = this._isCenterLineVisible;
            this._renderData.centerCurvePolylines.push(polyline);
        });
    }
    disposeIPPointSpheres() {
        this._renderData.ipPointSpheres.forEach((entity) => {
            this._viewer.entities.remove(entity);
        });
        this._renderData.ipPointSpheres.clear();
    }
    disposeIPPointLabels() {
        this._renderData.ipPointLabels.forEach((label) => {
            if (label) {
                this._viewer.entities.remove(label);
            }
        });
        this._renderData.ipPointLabels.clear();
    }
    disposeCenterLinePointSpheres() {
        this._renderData.centerLineSpheres.forEach((entity) => {
            this._viewer.entities.remove(entity);
        });
        this._renderData.centerLineSpheres.clear();
    }
    disposeCenterLinePointLabels() {
        this._renderData.centerLineLabels.forEach((label) => {
            if (label) {
                this._viewer.entities.remove(label);
            }
        });
        this._renderData.centerLineLabels.clear();
    }
    disposeRenderedEntities() {
        this.disposeIPPointLabels();
        this.disposeIPPointSpheres();
        this.disposeCenterLinePointLabels();
        this.disposeCenterLinePointSpheres();
        if (!this._polylineCollection) {
            return;
        }
        if (this._renderData.ipLine &&
            this._polylineCollection.contains(this._renderData.ipLine)) {
            this._polylineCollection.remove(this._renderData.ipLine);
        }
        this._renderData.ipLine = undefined;
        this._renderData.centerCurvePolylines.forEach((p) => {
            if (p && this._polylineCollection.contains(p)) {
                this._polylineCollection.remove(p);
            }
        });
        this._polylineCollection.removeAll();
        this._renderData.centerCurvePolylines.length = 0;
    }
}
//# sourceMappingURL=cesium-renderable-alignment.js.map